import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@mui/system";

const rotateAnimation = keyframes`
    from {
        transform: rotate(360deg);
    }
    to {
        transform: rotate(0deg);
    }
`;

type StyleKeys =
    | "container"
    | "banner"
    | "bannerProcessing"
    | "bannerSuccess"
    | "bannerPartial"
    | "bannerError"
    | "iconWrapper"
    | "processingIcon"
    | "body"
    | "topRow"
    | "textWrapper"
    | "counter"
    | "actions"
    | "progressBar";

export function useMarketingActionFeedbackBannerStyles(): Record<
    StyleKeys,
    SystemStyleObject<Theme>
> {
    const theme = useTheme();

    return {
        container: {
            display: "flex",
            flexDirection: "column",
            pointerEvents: "none",
        },
        banner: {
            display: "flex",
            alignItems: "stretch",
            padding: theme.spacing(1, 2),
            gap: theme.spacing(1.5),
            minHeight: 40,
            pointerEvents: "auto",
            boxShadow: theme.shadows[1],
        },
        bannerProcessing: {
            backgroundColor: theme.palette.primary[50],
            "& .icon-wrapper .MuiSvgIcon-root path": {
                color: theme.palette.primary.main,
            },
        },
        bannerSuccess: {
            backgroundColor: theme.palette.success[50],
            "& .icon-wrapper .MuiSvgIcon-root path": {
                color: theme.palette.success.main,
            },
        },
        bannerPartial: {
            backgroundColor: theme.palette.warning[50],
            "& .icon-wrapper .MuiSvgIcon-root path": {
                color: theme.palette.warning.main,
            },
        },
        bannerError: {
            backgroundColor: theme.palette.error[50],
            "& .icon-wrapper .MuiSvgIcon-root path": {
                color: theme.palette.error.main,
            },
        },
        iconWrapper: {
            display: "flex",
            alignItems: "center",
            alignSelf: "stretch",
        },
        processingIcon: {
            animation: `${rotateAnimation} 1.5s ease-in-out infinite`,
        },
        body: {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
        },
        topRow: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1.5),
        },
        textWrapper: {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
        },
        counter: {
            whiteSpace: "nowrap",
            alignSelf: "flex-end",
        },
        actions: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(1),
            flexShrink: 0,
            marginLeft: "auto",
        },
        progressBar: {
            width: "100%",
        },
    };
}
